/**
 * Thin data-access layer for the Order Booking content writer panel.
 *
 * Mirrors `src/components/Designer/OrderBooking/api.js` exactly in shape:
 * the shared axios wrapper (`src/services/api.js`) resolves with the parsed
 * envelope `{ code, status, message, data }` on success, and with the raw
 * axios error *response* on an HTTP error (so the envelope sits under
 * `.data`). `unwrap` collapses both into "payload or throw" — including the
 * 404 this panel's `/detail` returns when the content writer was never
 * assigned this creative; it surfaces as a normal thrown Error, same as any
 * other failure.
 */

import API from '../../../services/api';
import axios from '../../../services/axios';
import { ENDPOINTS, PAGE_SIZE } from './config';

export const isCancel = (err) => axios.isCancel(err);

export const createCancelSource = () => axios.CancelToken.source();

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Pull the most useful message out of either envelope shape. */
export const errorMessageOf = (res, fallback = GENERIC_ERROR) => {
  if (!res) return fallback;
  if (res instanceof Error) return res.message || fallback;
  return res?.data?.message || res?.message || fallback;
};

const unwrap = (res, fallback) => {
  if (res && res.code === 200) return res.data;
  const err = new Error(errorMessageOf(res, fallback));
  err.payload = res;
  throw err;
};

const post = async ({ url, data, cancelToken, headers, onUploadProgress, fallback }) => {
  const res = await API({
    url,
    method: 'POST',
    responseType: 'json',
    data,
    cancelToken,
    headers,
    onUploadProgress,
  });
  return unwrap(res, fallback);
};

/** `{ assigned, picked, submitted, rejected, approved, total }` */
export const fetchCounts = (filters, cancelToken) =>
  post({
    url: ENDPOINTS.counts,
    cancelToken,
    fallback: 'Unable to load the tab counts.',
    data: {
      sub_type: filters?.sub_type || null,
      query: String(filters?.query || '').trim(),
      start_date: filters?.start_date || null,
      end_date: filters?.end_date || null,
    },
  });

/** `{ data: Row[], total, hasMore }` */
export const fetchList = ({ tab, page, limit = PAGE_SIZE, ...filters }, cancelToken) =>
  post({
    url: ENDPOINTS.list,
    cancelToken,
    fallback: 'Unable to load creatives.',
    data: {
      tab,
      page,
      limit,
      sub_type: filters?.sub_type || null,
      query: String(filters?.query || '').trim(),
      start_date: filters?.start_date || null,
      end_date: filters?.end_date || null,
      sort_by_latest_activity: !!filters?.sort_by_latest_activity,
    },
  });

/** A single Row. 404s (never assigned this creative) surface as a thrown Error. */
export const fetchDetail = (bsc_id, cancelToken) =>
  post({
    url: ENDPOINTS.detail,
    data: { bsc_id },
    cancelToken,
    fallback: 'Unable to load this creative.',
  });

/** `{ data: Log[] }` — oldest first. Shared timeline table with the designer panel. */
export const fetchTimeline = (bsc_id, cancelToken) =>
  post({
    url: ENDPOINTS.timeline,
    data: { bsc_id },
    cancelToken,
    fallback: 'Unable to load the activity for this creative.',
  });

/** `{ bsc_id, status: 2, logs: Log[] }`. Allowed only when status is 1 (Assigned). */
export const pickCreative = (bsc_id) =>
  post({ url: ENDPOINTS.pick, data: { bsc_id }, fallback: 'Unable to pick this creative.' });

/** `{ logs: Log[] }`. No status gate. */
export const sendMessage = (bsc_id, message) =>
  post({
    url: ENDPOINTS.message,
    data: { bsc_id, message },
    fallback: 'Unable to send your message.',
  });

/** `{ bsc_id, url, status, logs: Log[] }`. No status gate. */
export const uploadAttachment = ({ bsc_id, file, message, onUploadProgress }) => {
  const form = new FormData();
  form.append('files', file);
  form.append('bsc_id', bsc_id);
  if (message) form.append('message', message);
  return post({
    url: ENDPOINTS.uploadAttachment,
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
    fallback: 'Upload failed. Please try again.',
  });
};

/**
 * `{ bsc_id, status, logs: Log[] }`. Allowed at status 2 (Picked), 3
 * (Submitted — still under review) or 4 (Rejected — resubmitting); 409 at
 * status 1 or 5.
 */
export const submitVariations = (bsc_id, variations) =>
  post({
    url: ENDPOINTS.submitVariations,
    data: { bsc_id, variations },
    fallback: 'Unable to submit your drafts.',
  });
